import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  TrendingUp, 
  Building2, 
  DollarSign, 
  Target, 
  Shield,
  Users,
  Globe,
  Banknote,
  PieChart,
  BarChart3,
  Zap
} from "lucide-react";

const CapitalPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    animate: { transition: { staggerChildren: 0.1 } }
  };

  const services = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Strategic Portfolio Companies",
      description: "Carefully selected investments in 8 high-potential businesses across diverse sectors with proven growth trajectories."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Reinvestment Strategy",
      description: "$150,000 in realized gains strategically reinvested for compound growth and long-term wealth creation."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Risk Management",
      description: "Disciplined approach to capital preservation while pursuing aggressive growth opportunities in emerging markets."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Long-Term Vision",
      description: "Patient capital deployment focused on generational wealth building rather than short-term exits."
    }
  ];

  const stats = [
    { number: "$300K", label: "Assets Under Management" },
    { number: "8", label: "Portfolio Companies" },
    { number: "$150K", label: "Gains Reinvested" },
    { number: "0", label: "Exits (Long-term Focus)" }
  ];

  const portfolioCompanies = [
    {
      name: "Tech Innovation Co.",
      sector: "Technology",
      stage: "Growth",
      investment: "$45,000",
      status: "Performing",
      description: "Emerging technology platform with strong user growth"
    },
    {
      name: "Health & Wellness Brand",
      sector: "Consumer",
      stage: "Early",
      investment: "$32,000", 
      status: "Developing",
      description: "Direct-to-consumer wellness product line"
    },
    {
      name: "Financial Services Startup",
      sector: "FinTech",
      stage: "Seed",
      investment: "$28,000",
      status: "Growth Phase",
      description: "Innovative financial technology solution"
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Sacred Geometry Background */}
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 180, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='200' viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b' fill-opacity='0.1'%3E%3Cpath d='M100 20L180 60v80L100 180L20 140V60z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '200px 200px'
            }}
          />
        </div>

        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1507924912916-7ba10ba86757"
            alt="Investment and growth"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-yellow-900/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div 
              className="flex justify-center mb-8"
              variants={fadeInUp}
            >
              <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-2xl shadow-yellow-400/50">
                <Banknote className="w-12 h-12 text-black" />
              </div>
            </motion.div>

            <motion.h1 
              className="text-6xl md:text-8xl font-bold mb-6"
              variants={fadeInUp}
            >
              Nhalege <span className="text-yellow-400">Capital</span>
            </motion.h1>
            
            <motion.p 
              className="text-2xl md:text-3xl text-yellow-400 mb-8 font-light"
              variants={fadeInUp}
            >
              Investing for the Future
            </motion.p>
            
            <motion.p 
              className="text-xl text-white/80 max-w-4xl mx-auto mb-12 leading-relaxed"
              variants={fadeInUp}
            >
              Strategic investment engine managing $300,000 across 8 carefully selected portfolio companies. 
              With $150,000 in gains already reinvested, we're building generational wealth through patient 
              capital deployment and long-term vision.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center"
              variants={fadeInUp}
            >
              <Link 
                to="/contact"
                className="group bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-12 py-4 text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-yellow-400/40 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-2">
                  <span>Become an Investor</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              
              <Link 
                to="/contact"
                className="group border-2 border-yellow-400 text-yellow-400 px-12 py-4 text-lg font-bold rounded-full hover:bg-yellow-400 hover:text-black transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5" />
                  <span>Pitch Your Company</span>
                </div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {stats.map((stat, index) => (
              <motion.div 
                key={stat.label}
                className="text-center"
                variants={fadeInUp}
              >
                <div className="text-4xl md:text-5xl font-bold text-yellow-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-white/70 text-sm font-medium tracking-wider uppercase">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Mission Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.unsplash.com/photo-1576400883215-7083980b6193"
            alt="Tech circuit"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-5xl md:text-6xl font-bold mb-8">
                Investment <span className="text-yellow-400">Philosophy</span>
              </h2>
              <div className="space-y-6 text-lg text-white/80 leading-relaxed">
                <p>
                  Nhalege Capital operates with a long-term investment horizon, focusing on building 
                  generational wealth rather than pursuing quick exits or short-term gains.
                </p>
                <p>
                  With $300,000 under management across 8 portfolio companies, we've already generated 
                  $150,000 in gains that have been strategically reinvested for compound growth.
                </p>
                <p>
                  Our approach emphasizes <span className="text-yellow-400 font-semibold">
                  patient capital deployment, strategic diversification, and active value creation</span> 
                  through operational support and strategic guidance.
                </p>
              </div>
              
              <div className="mt-10 p-8 bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-400/20 rounded-2xl">
                <h3 className="text-2xl font-bold text-yellow-400 mb-4">Investment Principles</h3>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    <span>Long-term wealth accumulation over short-term exits</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    <span>Strategic reinvestment of all realized gains</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    <span>Diversification across high-potential sectors</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    <span>Active value creation through operational support</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              variants={fadeInUp}
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1592597844940-a4c69ceb4be6"
                  alt="Financial growth"
                  className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-3xl"></div>
                
                {/* Floating Stats */}
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gray-900 to-black border border-yellow-400/30 rounded-2xl p-6 backdrop-blur-lg">
                  <div className="flex items-center space-x-4">
                    <PieChart className="w-8 h-8 text-yellow-400" />
                    <div>
                      <div className="text-2xl font-bold text-white">$150K</div>
                      <div className="text-sm text-white/60">Reinvested</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Investment <span className="text-yellow-400">Strategy</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Four strategic pillars of our investment approach and portfolio management
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="group bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-black">{service.icon}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-yellow-400 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Portfolio Companies Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            animate={{ x: [0, 100, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b' fill-opacity='0.1'%3E%3Cpath d='M30 5L50 20v20L30 55L10 40V20z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Portfolio <span className="text-yellow-400">Companies</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Strategic investments across 8 high-potential companies in diverse sectors
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {portfolioCompanies.map((company, index) => (
              <motion.div
                key={company.name}
                className="group bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-lg border border-yellow-400/20 rounded-3xl p-8 hover:border-yellow-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="text-yellow-400 font-semibold text-sm">{company.sector}</div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    company.status === 'Performing' ? 'bg-green-400 text-black' :
                    company.status === 'Growth Phase' ? 'bg-blue-400 text-black' :
                    'bg-orange-400 text-black'
                  }`}>
                    {company.status}
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-yellow-400 transition-colors duration-300">
                  {company.name}
                </h3>
                
                <p className="text-white/70 mb-6 leading-relaxed">
                  {company.description}
                </p>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/60">Investment:</span>
                    <span className="text-yellow-400 font-bold">{company.investment}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Stage:</span>
                    <span className="text-white">{company.stage}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="text-center mt-12"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <p className="text-white/60 text-lg">
              + 5 additional portfolio companies in stealth mode
            </p>
          </motion.div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Investment <span className="text-yellow-400">Performance</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Track record of strategic capital deployment and value creation
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {[
              {
                title: "Capital Efficiency",
                value: "$300K",
                growth: "Under Management",
                icon: <DollarSign className="w-6 h-6" />,
                description: "Strategic capital deployment across diverse portfolio"
              },
              {
                title: "Value Creation",
                value: "$150K",
                growth: "Gains Reinvested",
                icon: <TrendingUp className="w-6 h-6" />,
                description: "All realized gains reinvested for compound growth"
              },
              {
                title: "Portfolio Strength",
                value: "8",
                growth: "Active Companies",
                icon: <BarChart3 className="w-6 h-6" />,
                description: "Diversified investments across high-potential sectors"
              }
            ].map((metric, index) => (
              <motion.div
                key={metric.title}
                className="group text-center bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-yellow-400/20 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center group-hover:shadow-lg group-hover:shadow-yellow-400/30 transition-all duration-300">
                  <span className="text-black">{metric.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{metric.title}</h3>
                <div className="text-4xl font-bold text-yellow-400 mb-2">{metric.value}</div>
                <div className="text-sm text-yellow-400/80 font-semibold mb-4">{metric.growth}</div>
                <p className="text-white/60 text-sm">{metric.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-yellow-400/10 via-black to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="text-5xl md:text-6xl font-bold mb-8"
              variants={fadeInUp}
            >
              Build <span className="text-yellow-400">Wealth</span> With Us
            </motion.h2>
            
            <motion.p 
              className="text-xl text-white/80 mb-12"
              variants={fadeInUp}
            >
              Join our long-term investment strategy. Whether you're an investor looking for strategic opportunities 
              or an entrepreneur seeking growth capital, we're building generational wealth together.
            </motion.p>
            
            <motion.div 
              className="space-y-6"
              variants={fadeInUp}
            >
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-yellow-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-yellow-400 mb-6">Investor Information Request</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    placeholder="Full Name"
                    className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                  />
                  <input 
                    type="email" 
                    placeholder="Email Address"
                    className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                  />
                  <input 
                    type="tel" 
                    placeholder="Phone Number"
                    className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                  />
                  <select className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-yellow-400">
                    <option value="">Investment Interest</option>
                    <option value="investor">Become an Investor</option>
                    <option value="entrepreneur">Seeking Investment</option>
                    <option value="partnership">Strategic Partnership</option>
                    <option value="consultation">Investment Consultation</option>
                  </select>
                </div>
                <Link 
                  to="/contact"
                  className="w-full mt-6 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-4 text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-yellow-400/40 transition-all duration-300 hover:scale-105 flex items-center justify-center"
                >
                  Request Investment Information
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 bg-gray-900 border-t border-yellow-400/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link 
              to="/"
              className="group flex items-center space-x-2 text-white/70 hover:text-yellow-400 transition-colors duration-300 mb-4 md:mb-0"
            >
              <ArrowRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
              <span>Back to Empire Overview</span>
            </Link>
            
            <Link 
              to="/foundation"
              className="text-yellow-400 font-semibold hover:text-white transition-colors duration-300"
            >
              Next: Wealth Foundation →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CapitalPage;